import React, { useCallback } from 'react';
// Components
import FormsRow, { FormsRowModifier } from '../FormsRow/FormsRow';
import FormDateTime from '../FormDateTime/FormDateTime';
import FormsColumn from '../FormsColumn/FormsColumn';
import FormCheckbox from '../FormCheckbox/FormCheckbox';

import { DateRangeConstraints } from '../../models/InputModels/DateRange/DateRangeValidator';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';

type FormDateRangeType = {
  input: any;
  id: string;
  labelStartDate: string;
  labelEndDate: string;
  labelIsAllDay?: string;
  formsRowModifier?: string;
  onBlur?: any;
  automaticallyAddOneHourToEndDate?: boolean;
};

export default function FormDateRange({
  input,
  id,
  labelStartDate,
  labelEndDate,
  labelIsAllDay,
  formsRowModifier,
  onBlur = () => {},
  automaticallyAddOneHourToEndDate,
}: FormDateRangeType) {
  const { t } = useSonTranslation('FormDateRange');

  const getValidationMessage = (issues: any[]) => {
    if (issues && issues.length > 0) {
      const issueAndMessage = {
        issuer: '',
        message: t(`startDateBeforeEndDate`),
      };
      switch (issues[0]) {
        // don't fill in message and issuer, so the browser specific messages take precedence
        case DateRangeConstraints.STARTDATE_IS_INVALID:
        case DateRangeConstraints.ENDDATE_IS_INVALID:
          issueAndMessage.issuer = '';
          issueAndMessage.message = '';
          break;
        case DateRangeConstraints.STARTDATE_BEFORE_ENDDATE:
          issueAndMessage.issuer = 'endDate';
          break;
        default:
          break;
      }
      return issueAndMessage;
    }
    return {};
  };

  const setNewEndDatePlusOneHour = useCallback(() => {
    const startDate = new Date(input.params.startDate.value);
    startDate.setHours(startDate.getHours() + 1);
    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    const localDate = new Date(startDate.getTime() - tzoffset);
    const newEndDateString = localDate.toISOString().split('.')[0];

    input.onChange(input.params.endDate.name, newEndDateString);
  }, [input]);

  return (
    <>
      {input.params.isAllDay && (
        <FormsRow>
          <FormCheckbox
            layout="switch"
            id={`checkbox-${id}`}
            key={`${input.params.isAllDay.name}-${'checkbox'}`}
            name={input.params.isAllDay.name}
            required={input.params.isAllDay.required}
            labelTitle={labelIsAllDay}
            checked={input.params.isAllDay.checked}
            value={input.params.isAllDay.value}
            onChange={input.params.isAllDay.onChange}
          />
        </FormsRow>
      )}
      <FormsRow
        key={FormsRowModifier.TWO_DATETIMES}
        modifier={
          formsRowModifier ? [formsRowModifier, FormsRowModifier.TWO_DATETIMES] : FormsRowModifier.TWO_DATETIMES
        }
      >
        <FormsColumn>
          <FormDateTime
            name={input.params.startDate.name}
            required={input.params.startDate.required}
            value={input.params.startDate.value}
            label={labelStartDate}
            validationMessageMapper={getValidationMessage(input.issues)}
            isAllDay={input.params.isAllDay?.checked}
            onChange={(event: any) => {
              input.onChange(event.target.name, event.target.value);
            }}
            onBlur={automaticallyAddOneHourToEndDate ? setNewEndDatePlusOneHour : undefined}
          />
        </FormsColumn>
        <FormsColumn>
          <FormDateTime
            name={input.params.endDate.name}
            required={input.params.endDate.required}
            value={input.params.endDate.value}
            label={labelEndDate}
            validationMessageMapper={getValidationMessage(input.issues)}
            isAllDay={input.params.isAllDay?.checked}
            // Events
            onChange={(event) => {
              input.onChange(event.target.name, event.target.value);
            }}
            onBlur={onBlur}
          />
        </FormsColumn>
      </FormsRow>
    </>
  );
}
